import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import ArrowRight from '@/assets/icons/arrow-right.svg?react'
import { Button, ButtonRadio, Loading, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { Pagination } from '@/components/base/Pagination'
import { TeamRole } from '@/consts/team'
import { getBonusStat } from '@/models/credit'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatDurationToHours, formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { BalanceDetailModal } from '../../billing/components/BalanceDetailModal'
import { ExchargeModal } from '../../billing/components/RechargeModal'
import { MemberFilter } from '../components/MemberFilter'
import { BillingTable } from './components/BillingTable'
import { ViewMode, ViewSwitcher } from './components/ViewSwitcher'

export function TeamBillingPage() {
  const [loading, setLoading] = useState(false)
  const [catalog, setCatalog] = useState(0)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [bills, setBills] = useState<any[]>([])
  const [viewMode, setViewMode] = useState<ViewMode>('member')
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false)
  const [balanceDetailOpen, setBalanceDetailOpen] = useState(false)
  const latestRequestIdRef = useRef(0)

  const pageSize = 10
  const teamStore = useTeamStore()
  const userStore = useUserStore()
  const { t } = useTranslation('billing')

  const teamTokenData = teamStore.teamTokenData
  const userRole = teamTokenData?.userRole || TeamRole.Normal

  // Check permissions
  const canSwitchView = userRole === TeamRole.Owner || userRole === TeamRole.Admin
  const canRecharge = userRole === TeamRole.Owner
  const isAdminView = canSwitchView && viewMode === 'admin'

  const catalogItems = useMemo(() => {
    return [
      {
        label: t('type.all'),
        value: 0,
      },
      {
        label: t('type.instances'),
        value: 1,
      },
      {
        label: t('type.others'),
        value: 2,
      },
    ]
  }, [t])

  const overviewItems = useMemo(() => {
    const hasBonusStat = !!getBonusStat(userStore.account)

    return [
      {
        key: 'balance',
        label: t('balanceTitle'),
        value: formatPrice(userStore.account.totalBonus.moneyCent ?? 0, false, ''),
        // every member may inspect the breakdown; without bonusStat fall back to
        // the previous owner-only recharge behavior
        action:
          hasBonusStat || canRecharge
            ? () => {
                if (hasBonusStat) {
                  setBalanceDetailOpen(true)
                } else {
                  setRechargeModalOpen(true)
                }
              }
            : undefined,
      },
      {
        key: 'total-spending',
        label: t('totalSpending'),
        value: formatPrice(userStore.account.expense.amount ?? 0, false, ''),
      },
      {
        key: 'total-duration',
        label: t('duration'),
        value: formatDurationToHours(userStore.account.expense.timeSec ?? 0),
      },
    ]
  }, [userStore, t, canRecharge])

  const handleViewModeChange = (mode: ViewMode) => {
    if (mode === viewMode) return

    // Hide stale rows immediately so the new column layout never renders old-mode data.
    latestRequestIdRef.current += 1
    setLoading(true)
    setBills([])
    setTotal(0)
    setCatalog(0)
    setSelectedMembers([])
    setPage(1)
    setViewMode(mode)
  }

  const fetchBill = useCallback(
    async (
      p: number,
      filterCatalog: number,
      filterMembers: string[],
      currentViewMode: ViewMode,
    ) => {
      if (!teamTokenData?.teamResID) return

      const requestId = ++latestRequestIdRef.current
      const isAdmin = canSwitchView && currentViewMode === 'admin'

      setLoading(true)
      try {
        let res

        if (isAdmin) {
          res = await API.getTeamMemberBills({
            res_id: teamTokenData.teamResID,
            page: p,
            per_page: pageSize,
            catalog: filterCatalog,
            member_res_ids: filterMembers.length > 0 ? filterMembers : undefined,
          })
        } else {
          res = await API.getUserBills(
            {
              page: p,
              per_page: pageSize,
              catalog: filterCatalog,
            },
            {},
          )
        }

        if (requestId !== latestRequestIdRef.current) return

        if (res.code === 0) {
          setBills(res.bills || [])
          setTotal(res.pagination?.total || 0)
        }
      } catch (error) {
        if (requestId === latestRequestIdRef.current) {
          console.error(error)
        }
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setLoading(false)
        }
      }
    },
    [teamTokenData?.teamResID, canSwitchView],
  )

  useEffect(() => {
    fetchBill(page, catalog, selectedMembers, viewMode)
  }, [fetchBill, page, catalog, selectedMembers, viewMode])

  return (
    <div className="flex flex-col h-full">
      {/* Metrics Card */}
      <div className="card p-4 overflow-clip bg-panel-white">
        <div className="grid grid-cols-3 text-secondary gap-x-4">
          {overviewItems.map((item) => (
            <div
              key={item.key}
              className={cn('border border-neutral-100 p-6 rounded-2xl', {
                'cursor-pointer': item.action,
              })}
              onClick={item.action}
            >
              <div className="t-x-sb text-secondary mb-2">{item.label}</div>
              <div className="flex items-center gap-x-4">
                <div className="h5 text-primary flex-grow">{item.value}</div>
                {item.action && <ArrowRight width={20} height={20} />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Card */}
      <div className="card bg-panel-white h-full overflow-y-auto mt-4">
        {/* Header */}
        <div className="flex gap-x-8 h6 items-center justify-between border-b border-neutral-200 pb-4 mb-12">
          <div className="flex items-center gap-2">
            <h6 className="text-primary">
              <Trans ns="billing" i18nKey="billingTab">
                My Spending
              </Trans>
            </h6>
            {canSwitchView && <ViewSwitcher value={viewMode} onChange={handleViewModeChange} />}
          </div>
          {canRecharge && (
            <Button type="secondary" onClick={() => setRechargeModalOpen(true)}>
              <Trans ns="billing" i18nKey="rechargeBtn">
                Recharge
              </Trans>
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-end gap-2">
          {isAdminView && <MemberFilter value={selectedMembers} onChange={setSelectedMembers} />}
          <div className="flex flex-col gap-2">
            {isAdminView ? (
              <Dropdown
                rootClassName="w-[180px]"
                options={catalogItems}
                value={catalog}
                onChange={(v) => {
                  setPage(1)
                  setCatalog(v as number)
                }}
              />
            ) : (
              <ButtonRadio
                items={catalogItems}
                unselectedClassName={'text-tertiary'}
                onChange={(v) => {
                  setPage(1)
                  setCatalog(v)
                }}
              />
            )}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <>
            <BillingTable bills={bills} showMemberColumn={isAdminView} />
            <Pagination
              size={pageSize}
              total={total}
              current={page}
              className="justify-end mt-4"
              onChange={setPage}
            />
          </>
        )}
      </div>

      {/* Balance Detail Modal */}
      {balanceDetailOpen && (
        <BalanceDetailModal
          onClose={() => setBalanceDetailOpen(false)}
          onRecharge={
            canRecharge
              ? () => {
                  setBalanceDetailOpen(false)
                  setRechargeModalOpen(true)
                }
              : undefined
          }
        />
      )}

      {/* Recharge Modal */}
      {rechargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setRechargeModalOpen(false)
            fetchBill(page, catalog, selectedMembers, viewMode)
          }}
          onClose={() => {
            setRechargeModalOpen(false)
          }}
        />
      )}
    </div>
  )
}
